import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Users, Settings, Activity, Search, Building2, UserCheck, Copy } from 'lucide-react';
import SMTPSettings from '../components/SMTPSettings';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { saveAccountsCache } from '../lib/impersonation';

type Facility = {
  type: string;
  facility_name: string | null;
  location_id: string | null;
  status: string;
};

type Account = {
  user_id: string;
  email: string | null;
  last_sign_in_at: string | null;
  facilities: Facility[];
};

async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function fetchAccounts(q: string): Promise<{ accounts: Account[]; total: number }> {
  const token = await getAccessToken();
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', '50');
  const res = await fetch(`/.netlify/functions/admin-list-accounts?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.error || `Failed to load accounts (${res.status})`);
  }
  const accounts = json.accounts || [];
  if (!q && accounts.length > 0) {
    saveAccountsCache(accounts);
  }
  return { accounts, total: json.total ?? 0 };
}

function facilityLabel(facilities: Facility[]): string {
  if (!facilities?.length) return '—';
  return facilities
    .map((f) => {
      const name = f.facility_name || f.type;
      return f.location_id ? `${name} · ${f.location_id}` : name;
    })
    .join(', ');
}

function statusSummary(facilities: Facility[]): string {
  if (!facilities?.length) return 'None';
  return facilities.map((f) => `${f.type === 'zonedm' ? 'GHL' : f.type} (${f.status})`).join(', ');
}

export default function AdminDashboard() {
  const { isSuperAdmin, startImpersonation } = useAuth();
  const [searchInput, setSearchInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [openingId, setOpeningId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-dashboard-accounts', q],
    queryFn: () => fetchAccounts(q),
    enabled: isSuperAdmin,
  });

  const accounts = data?.accounts || [];
  const totalUsers = data?.total ?? accounts.length;
  const totalIntegrations = accounts.reduce((acc, a) => acc + (a.facilities?.length || 0), 0);
  const activeIntegrations = accounts.reduce(
    (acc, a) => acc + (a.facilities?.filter((f) => f.status === 'active').length || 0),
    0
  );

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success('User ID copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const openAs = async (account: Account) => {
    if (!account.email) {
      toast.error('Account has no email');
      return;
    }
    setOpeningId(account.user_id);
    try {
      await startImpersonation({
        target_user_id: account.user_id,
        target_email: account.email,
        reason: 'Opened from admin dashboard overview',
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to open as customer');
      setOpeningId(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-sm text-gray-600 mb-6">
          Account listing requires superadmin. If you just received access, sign out and sign back in.
        </p>
        <SMTPSettings />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Clinic accounts, integrations, and system settings
          </p>
        </div>
        <Link
          to="/admin/accounts"
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 min-h-[44px]"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Accounts switcher
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Clinic accounts</p>
              <p className="text-2xl font-semibold mt-1">{isLoading ? '…' : totalUsers}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active integrations</p>
              <p className="text-2xl font-semibold mt-1">
                {isLoading ? '…' : activeIntegrations}
                <span className="text-sm font-normal text-gray-400 ml-1">/ {totalIntegrations}</span>
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Settings className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Showing</p>
              <p className="text-2xl font-semibold mt-1">
                {isLoading ? '…' : accounts.length}
                <span className="text-sm font-normal text-gray-400 ml-1">of {totalUsers}</span>
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">System status</p>
              <p className="text-2xl font-semibold mt-1 text-green-700">Healthy</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">System settings</h2>
        <SMTPSettings />
      </div>

      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-lg font-medium text-gray-900">Users and integrations</h2>
        <p className="text-xs text-gray-500">Same data as Accounts — open a customer to manage their setup</p>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search users by email or facility..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        />
      </div>

      <Card className="overflow-hidden">
        {isLoading || isFetching ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-red-600 mb-3">
              {(error as Error)?.message || "Couldn't load accounts."}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              If this says Superadmin required, sign out and back in after bootstrap.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              Retry
            </button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-600 mb-3">
              No accounts match. Try another email or facility name.
            </p>
            {q && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setQ('');
                }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Integrations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last sign-in
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => (
                  <tr key={account.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {account.email || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {facilityLabel(account.facilities)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {statusSummary(account.facilities)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.last_sign_in_at
                        ? new Date(account.last_sign_in_at).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        type="button"
                        disabled={openingId === account.user_id}
                        onClick={() => openAs(account)}
                        className="inline-flex items-center min-h-[40px] px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 mr-2"
                      >
                        <UserCheck className="w-4 h-4 mr-1.5" />
                        {openingId === account.user_id ? 'Opening…' : 'Open as'}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyId(account.user_id)}
                        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 min-h-[40px] px-2"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        ID
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
